<template>
  <div class="schedule-posts-modal">
    <div class="d-flex flex-row justify-content-center align-items-center" v-if="state === 'LOADING'">
      <b-spinner/>
    </div>
    <div class="d-flex flex-row justify-content-center align-items-center" v-else-if="state === 'ERROR'">
      <b-alert variant="danger">{{ err.userMessage || err.message }}</b-alert>
    </div>

    <div class="d-flex flex-column" v-else-if="state === 'CHOOSE-POSTING-TIMES'">
      <h4>{{ $t('campaign.scheduled-posts.first-step-title') }}</h4>
      <ul class="pl-4">
        <li>{{ $t('campaign.scheduled-posts.first-step-first-line') }}</li>
        <li>{{ $t('campaign.scheduled-posts.first-step-second-line') }}</li>
        <li>{{ $t('campaign.scheduled-posts.first-step-third-line') }}</li>
      </ul>

      <scheduled-post-preset-picker :labels="presetLabels" :presets="presetTimes" show-custom v-model="scheduledPostTimes"/>

      <!-- <b-alert v-if="scheduledPostTimes === null && scheduledPosts.length" variant="warning">
        <strong>This will reset any existing scheduled posts</strong> - are you sure?
      </b-alert> -->

      <div class="d-flex flex-row justify-content-end align-items-center mt-3">
        <b-btn :disabled="!scheduledPostTimes" @click="saveScheduledPostTimes" variant="primary">{{ $t('campaign.scheduled-posts.next') }}</b-btn>
      </div>
    </div>

    <div class="d-flex flex-column" v-else-if="state === 'SELECT-ITEMS'">
      <h4>{{ $t('campaign.scheduled-posts.second-step-title') }}</h4>
      <b-list-group class="my-2" v-if="api && (Array.isArray(api.jobs) || Array.isArray(api.contentShares))">
        <b-list-group-item class="d-flex flex-row justify-content-end align-items-center py-1">
          <b-btn class="m-1" size="sm" :title="$t('campaign.scheduled-posts.edit-default-templates')" v-b-toggle.edit-global-templates variant="transparent">
            <font-awesome-icon icon="font" size="sm"/>
          </b-btn>
          <b-btn class="border-0 rounded-circle m-1" size="sm" title="Remove All" variant="outline-danger" :style="{ 'min-width': '30px' }"
            v-if="selectedItems.length" @click="deselectAllItems">
              <font-awesome-icon icon="times" size="sm"/>
          </b-btn>
          <b-btn class="border-0 rounded-circle m-1" size="sm" title="Select All" variant="outline-success" :style="{ 'min-width': '30px' }"
            v-else @click="selectAllItems">
              <font-awesome-icon icon="check" size="sm"/>
          </b-btn>
        </b-list-group-item>
        <b-collapse id="edit-global-templates">
          <b-list-group-item class="d-flex flex-column justify-content-end align-items-center">
            <h4 class="w-100">{{ $t('campaign.scheduled-posts.edit-default-templates') }}</h4>
            <b-form-group class="w-100">
              <b-form-textarea id="jobShareTemplate" v-model="jobShareTemplate"/>
              <b-form-text><strong class="text-monospace">{jobTitle}</strong> - {{ $t('campaign.scheduled-posts.placeholder') }}</b-form-text>
            </b-form-group>
            <b-form-group class="w-100">
              <b-form-textarea id="contentShareTemplate" v-model="contentShareTemplate"/>
              <b-form-text><strong class="text-monospace">{contentTitle}</strong> - {{ $t('campaign.scheduled-posts.placeholder') }}</b-form-text>
            </b-form-group>
          </b-list-group-item>
        </b-collapse>

        <draggable handle=".drag-handle" v-model="selectedItems">
          <transition-group>
            <b-list-group-item class="d-flex flex-row align-items-start" v-for="item in selectedItems" :key="item.id">
              <div class="drag-handle flex-shrink-0 h-100 pt-1" :style="{ width: '20px' }">
                <font-awesome-icon icon="bars" size="sm"/>
              </div>
              <div class="flex-fill d-flex flex-column text-truncate mr-3">
                <p class="font-weight-bold text-truncate m-0">{{ item.entry.title }}</p>
                <job-key-stats v-if="item.type === 'Job'" :job="item.entry" include-type hide-labels hide-referral-bonus iconSize="lg"/>
                <content-key-stats v-if="item.type === 'ContentShare'" :contentShare="item.entry" include-type hide-labels iconSize="lg"/>
                <div class="d-flex flex-row align-items-center">
                  <p class="flex-fill text-truncate m-0" v-if="shareTextOverrides[`${item.type}-${item.id}`]">{{ shareTextOverrides[`${item.type}-${item.id}`] }}</p>
                  <p class="flex-fill font-italic text-truncate m-0" v-else>
                    <template v-if="item.type === 'Job'">{{ createTemplateString(jobShareTemplate, item.vars) }}</template>
                    <template v-if="item.type === 'ContentShare'">{{ createTemplateString(contentShareTemplate, item.vars) }}</template>
                  </p>
                  <b-btn class="flex-shrink-0 ml-1 p-0" size="sm" title="Edit Text" v-b-toggle="`edit-template-${item.id}`" variant="transparent">
                    <font-awesome-icon icon="pencil-alt" size="sm"/>
                  </b-btn>
                </div>
                <b-collapse :id="`edit-template-${item.id}`">
                  <b-form-group class="w-100">
                    <b-form-textarea :placeholder="$t('campaign.scheduled-posts.share-text-input')" v-model="shareTextOverrides[`${item.type}-${item.id}`]"/>
                  </b-form-group>
                </b-collapse>
              </div>
              <b-btn class="flex-shrink-0 border-0 rounded-circle m-1" size="sm" variant="outline-danger" :style="{ 'min-width': '30px' }"
                title="Remove Item" @click="() => toggleSelectedItem(item.type, item.id)">
                  <font-awesome-icon icon="times" size="sm"/>
              </b-btn>
            </b-list-group-item>
          </transition-group>
        </draggable>

        <template v-if="unselectedItems && (Array.isArray(unselectedItems.jobs) || Array.isArray(unselectedItems.contentShares))">
          <b-list-group-item class="d-flex flex-row align-items-start" v-for="job in unselectedItems.jobs" :key="job.id">
            <div class="flex-shrink-0" :style="{ width: '20px' }"/>
            <div class="flex-fill d-flex flex-column text-truncate mr-3">
              <p class="font-weight-bold text-truncate m-0">{{ job.title }}</p>
              <job-key-stats :job="job" include-type hide-labels hide-referral-bonus iconSize="lg"/>
            </div>
            <b-btn class="flex-shrink-0 rounded-circle m-1" size="sm" variant="outline-success" :style="{ 'min-width': '30px' }" :title="$t('campaign.scheduled-posts.select-job')"
              @click="() => toggleSelectedItem('Job', job.id, job)">
                <font-awesome-icon icon="plus" size="sm"/>
            </b-btn>
          </b-list-group-item>
          <b-list-group-item class="d-flex flex-row align-items-start" v-for="contentShare in unselectedItems.contentShares" :key="contentShare.id">
            <div class="flex-shrink-0" :style="{ width: '20px' }"/>
            <div class="flex-fill d-flex flex-column text-truncate mr-3">
              <span class="d-block font-weight-bold text-truncate">{{ contentShare.title }}</span>
              <content-key-stats :contentShare="contentShare" include-type hide-labels iconSize="lg"/>
            </div>
            <b-btn class="flex-shrink-0 rounded-circle m-1" size="sm" variant="outline-success" :style="{ 'min-width': '30px' }" :title="$t('campaign.scheduled-posts.select-content')"
              @click="() => toggleSelectedItem('ContentShare', contentShare.id, contentShare)">
                <font-awesome-icon icon="plus" size="sm"/>
            </b-btn>
          </b-list-group-item>
        </template>
      </b-list-group>

      <div class="d-flex flex-row justify-content-between align-items-center mt-3">
        <b-btn @click="() => state = 'CHOOSE-POSTING-TIMES'" variant="transparent">{{ $t('campaign.scheduled-posts.back') }}</b-btn>
        <b-btn :disabled="!selectedItems.length" @click="saveScheduledPosts" variant="primary">{{ $t('campaign.scheduled-posts.schedule') }}</b-btn>
      </div>
    </div>

    <div class="d-flex flex-column" v-else-if="state === 'FINISHED'">
      <div class="d-flex flex-column justify-content-center align-items-center w-100">
        <h6>{{ $t('campaign.scheduled-posts.third-step-title') }}</h6>
        <p>{{ $t('campaign.scheduled-posts.third-step-description') }}</p>
      </div>
      <div class="d-flex flex-row justify-content-between align-items-center mt-3">
        <b-btn @click="() => state = 'CUSTOMISE-SHARES'" variant="transparent">{{ $t('campaign.scheduled-posts.back') }}</b-btn>
        <b-btn @click="() => $emit('modal-close')" variant="primary">{{ $t('campaign.scheduled-posts.close') }}</b-btn>
      </div>
    </div>

  </div>
</template>

<script>
import _chunk from 'lodash/chunk';
import assert from 'http-assert-plus';
import ScheduledPostPresetPicker from '@src/components/users/ScheduledPostPresetPicker';
import JobKeyStats from '@src/components/jobs/key-stats';
import ContentKeyStats from '@src/components/content-share/KeyStats';
import gql from 'graphql-tag';
import Draggable from 'vuedraggable';
import { mapRoot, mapPreferences } from '@src/mixins/root';
// import { prepareScheduledTimes } from '@src/plugins/next-time';
import { UserScheduledPostDetailsFragment } from '@src/graphql';

const SchedulePostsListQuery = gql`
  query SchedulePostsList(
    $includeJobs: Boolean = false
    $jobIDs: [ ID! ],
    $includeContentShares: Boolean = false
    $contentShareIDs: [ ID! ],
    $sentPostsWhere: UserScheduledPostWhereManyInput,
    $queuedPostsWhere: UserScheduledPostWhereManyInput,
    $isCampaign: Boolean = false,
    $campaignWhere: CampaignWhereOneInput!
  ) {
    company {
      id
      jobs(where: { id_in: $jobIDs }) @include(if: $includeJobs) {
        id
        title
        shareCode
        location {
          id
          name
        }
        customFields {
          key
          name
          value
        }
      }
      contentShares(where: { id_in: $contentShareIDs }) @include(if: $includeContentShares) {
        id
        title
        url
        shareCode
      }
    }
    user {
      id
      scheduledPostTimes { mon tue wed thu fri sat sun }
      sentPosts: scheduledPosts(where: $sentPostsWhere) {
        ...UserScheduledPostDetails
      }
      queuedPosts: scheduledPosts(where: $queuedPostsWhere) {
        ...UserScheduledPostDetails
      }
      campaign(where: $campaignWhere) @include(if:$isCampaign) {
        id
        trackingCode
      }
    }
  }
  ${UserScheduledPostDetailsFragment}
`;
const SaveSchedulePostsTimesMutation = gql`
  mutation SaveSchedulePostsTimes($times: UserScheduledPostTimesInput) {
    updateUserScheduledPostTimes(user: { self: true }, times: $times)
  }
`;
// const AddSchedulePostMutation = gql`
//   mutation AddSchedulePost($create: CreateScheduledPostInput!) {
//     createUserScheduledPost(create: $create) {
//       ...UserScheduledPostDetails
//     }
//   }
//   ${UserScheduledPostDetailsFragment}
// `;
// const RemoveSchedulePostMutation = gql`
//   mutation AddSchedulePost($where: UserScheduledPostWhereOneInput!) {
//     deleteUserScheduledPost(where: $where)
//   }
// `;
const SaveSchedulePostsMutation = gql`
  mutation SaveSchedulePosts(
    $withDelete: Boolean! = false,
    $deleteIDs: [ ID! ],
    $create: [CreateScheduledPostInput!]!,
  ) {
    deleteManyUserScheduledPosts(where: { id_in: $deleteIDs }) @include(if: $withDelete)
    createManyUserScheduledPosts(create: $create) {
      ...UserScheduledPostDetails
    }
  }
  ${UserScheduledPostDetailsFragment}
`;

export default {
  props: {
    eventID: String,
    campaignID: String,
    jobIDs: Array,
    contentShareIDs: Array,
  },
  components: {
    ScheduledPostPresetPicker,
    JobKeyStats,
    ContentKeyStats,
    Draggable,
  },
  data() {
    return {
      jobShareTemplate: this.$t('campaign.scheduled-posts.job-template'),
      // jobShareTokens: { Title: 'The job title' },
      contentShareTemplate: this.$t('campaign.scheduled-posts.content-template'),
      // contentShareTokens: { Title: 'The content title' },
      shareTextOverrides: null,
      scheduledPostTimes: null,
      state: null,
      selectedJobIDs: null,
      selectedContentShareIDs: null,
      selectedItems: null,
      waiting: false,
      trackingCode: null,
    };
  },
  created() {
    this.scheduledPostTimes = null;
    this.state = 'LOADING';
    this.selectedJobIDs = [];
    this.selectedContentShareIDs = [];
    this.selectedItems = [];
    this.shareTextOverrides = {};

    const { company } = this.$root || {};
    const { sitePreferences } = company || {};
    const { jobShareText, contentShareText } = sitePreferences || {};
    this.jobShareTemplate = jobShareText || this.jobShareTemplate.replace('{companyName}', this.companyName);
    this.contentShareTemplate = contentShareText || this.contentShareTemplate.replace('{companyName}', this.companyName);
  },
  apollo: {
    api: {
      query: SchedulePostsListQuery,
      variables() {
        const { jobIDs, contentShareIDs, campaignID } = this;
        console.log({ campaignID });
        return {
          includeJobs: Boolean(Array.isArray(jobIDs) && jobIDs.length),
          jobIDs: Array.isArray(jobIDs) ? jobIDs : null,
          includeContentShares: Boolean(Array.isArray(contentShareIDs) && contentShareIDs.length),
          contentShareIDs: Array.isArray(contentShareIDs) ? contentShareIDs : null,
          sentPostsWhere: {
            jobs: Array.isArray(jobIDs) && jobIDs.length > 0 ? { id_in: jobIDs } : undefined,
            contentShares: Array.isArray(contentShareIDs) ? { id_in: contentShareIDs } : undefined,
            sendAt_lte: 'NOW',
          },
          queuedPostsWhere: {
            jobs: Array.isArray(jobIDs) && jobIDs.length > 0 ? { id_in: jobIDs } : undefined,
            contentShares: Array.isArray(contentShareIDs) ? { id_in: contentShareIDs } : undefined,
            sendAt_gte: 'NOW',
          },
          isCampaign: !!campaignID,
          campaignWhere: campaignID ? { id: campaignID } : {}
        };
      },
      update({ company, user }) {
        const { jobs, contentShares } = company || {};
        const { sentPosts, queuedPosts } = user || {};
        return { jobs, contentShares, sentPosts, queuedPosts };
      },
      result({ loading, data }) {
        if (!loading && data && data.company && data.user && data.user.id) {
          const { scheduledPostTimes } = data.user;
          this.scheduledPostTimes = scheduledPostTimes;

          // this.selectedJobIDs = this.scheduledPosts.reduce((list, { job }) =>
          //   job && job.id ? list.concat(job.id) : list, []);
          // this.selectedContentShareIDs = this.scheduledPosts.reduce((list, { contentShare }) =>
          //   contentShare && contentShare.id ? list.concat(contentShare.id) : list, []);

          // this.state = this.scheduledPostTimes ? 'SELECT-ITEMS' : 'CHOOSE-POSTING-TIMES';
          this.state = 'CHOOSE-POSTING-TIMES';

          console.log({
            user: data.user
          });

          if (data.user.campaign) {
            const { trackingCode } = data.user.campaign;
            this.trackingCode = trackingCode;
          }
        }
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'companyName', 'origin' ]),
    ...mapPreferences([ 'jobShareText', 'contentShareText' ]),
    presetLabels() {
      return {
        'EVERY-DAY-9AM-12PM-3PM': this.$t('campaign.scheduled-posts.EVERY-DAY-9AM-12PM-3PM'),
        'EVERY-DAY-9AM-3PM': this.$t('campaign.scheduled-posts.EVERY-DAY-9AM-3PM'),
        'EVERY-DAY-10AM': this.$t('campaign.scheduled-posts.EVERY-DAY-10AM'),
        'EVERY-OTHER-DAY-10AM': this.$t('campaign.scheduled-posts.EVERY-OTHER-DAY-10AM'),
      };
    },
    presetTimes: () => ({
      'EVERY-DAY-9AM-12PM-3PM': {
        mon: [ 540, 720, 900 ],
        tue: [ 540, 720, 900 ],
        wed: [ 540, 720, 900 ],
        thu: [ 540, 720, 900 ],
        fri: [ 540, 720, 900 ],
      },
      'EVERY-DAY-9AM-3PM': {
        mon: [ 540, 900 ],
        tue: [ 540, 900 ],
        wed: [ 540, 900 ],
        thu: [ 540, 900 ],
        fri: [ 540, 900 ],
      },
      'EVERY-DAY-10AM': {
        mon: [ 600 ],
        tue: [ 600 ],
        wed: [ 600 ],
        thu: [ 600 ],
        fri: [ 600 ],
      },
      'EVERY-OTHER-DAY-10AM': {
        mon: [ 600 ],
        wed: [ 600 ],
        fri: [ 600 ],
      },
    }),
    unselectedItems() {
      const { selectedItems } = this;
      const { jobs, contentShares } = this.api || {};
      return {
        jobs: Array.isArray(jobs) ? jobs.filter(({ id }) => !selectedItems.find(r => r && r.type === 'Job' && r.id === id)) : [],
        contentShares: Array.isArray(contentShares) ? contentShares.filter(({ id }) => !selectedItems.find(r => r && r.type === 'ContentShare' && r.id === id)) : [],
      };
    },
    previouslySent() {
      const { sentPosts } = this.api || {};
      return sentPosts.reduce((list, post) => {
        let type = null;
        let id = null;

        if (post && post.job && post.job.id) {
          type = 'Job';
          ({ id } = post.job);
        } else if (post && post.contentShare && post.contentShare.id) {
          type = 'ContentShare';
          ({ id } = post.contentShare);
        }

        if (type && id) {
          const { sentAt } = post;

          if (list[`${type}-${id}`]) {
            const { count, lastSentAt } = list[`${type}-${id}`];
            list[`${type}-${id}`].count = count + 1;
            list[`${type}-${id}`].lastSentAt = lastSentAt > sentAt ? sentAt : lastSentAt;
          } else {
            list[`${type}-${id}`] = { count: 1, lastSentAt: sentAt };
          }
        }

        return list;
      }, {});
    },
    // scheduleList() {
    //   const { scheduledPostTimes, selectedJobIDs, selectedContentShareIDs, eventID, origin } = this;

    //   const nextTime = scheduledPostTimes ? prepareScheduledTimes(scheduledPostTimes, new Date()) : null;
    //   const jobSharePrefix = eventID ? `/e/r/${eventID}/` : '/r/';
    //   const contentSharePrefix = eventID ? `/e/c/${eventID}/` : '/c/';

    //   const { jobs, contentShares } = this.items || {};
    //   const items = [];

    //   (Array.isArray(jobs) ? jobs : []).forEach(({ id, title, shareCode }) => {
    //     items.push({
    //       __typename: 'Job',
    //       // eslint-disable-next-line object-property-newline
    //       id, title, shareCode,
    //       postID: Array.isArray(this.scheduledPosts)
    //         ? (this.scheduledPosts.find(p => p && p.job && p.job.id === id) || {}).id || null
    //         : null,
    //       shareURL: `${origin}${jobSharePrefix}${shareCode}`,
    //     });
    //   });

    //   (Array.isArray(contentShares) ? contentShares : []).forEach(({ id, title, shareCode }) => {
    //     items.push({
    //       __typename: 'ContentShare',
    //       // eslint-disable-next-line object-property-newline
    //       id, title, shareCode,
    //       postID: Array.isArray(this.scheduledPosts)
    //         ? (this.scheduledPosts.find(p => p && p.contentShare && p.contentShare.id === id) || {}).id || null
    //         : null,
    //       shareURL: `${origin}${contentSharePrefix}${shareCode}`,
    //     });
    //   });

    //   if (nextTime && (selectedJobIDs.length || selectedContentShareIDs.length)) {
    //     const [ j1, j2, j3, j4, j5, j6, j7, j8, j9, j10, j11, j12, j13, j14, j15, j16, j17, j18, j19, j20, ...jRest ] = selectedJobIDs;
    //     const [ c1, c2, c3, c4, c5, c6, c7, c8, c9, c10, ...cRest ] = this.selectedContentShareIDs;
    //     [
    //       c1, j1, j2,
    //       c2, j3, j4,
    //       c3, j5, j6,
    //       c4, j7, j8,
    //       c5, j9, j10,
    //       c6, j11, j12,
    //       c7, j13, j14,
    //       c8, j15, j16,
    //       c9, j17, j18,
    //       c10, j19, j20,
    //       ...cRest, ...jRest,
    //     ].filter(v => Boolean(v)).forEach(id => {
    //       const i = items.findIndex(row => row && row.id === id);
    //       // eslint-disable-next-line no-unused-expressions
    //       i >= 0 ? items[i].sendAt = nextTime() : undefined;
    //     });
    //   }

    //   return items;
    // },
  },
  methods: {
    async saveScheduledPostTimes() {
      try {
        const times = JSON.parse(JSON.stringify(this.scheduledPostTimes));
        delete times.__typename;
        this.$log.debug({ times });

        await this.$apollo.mutate({
          mutation: SaveSchedulePostsTimesMutation,
          variables: { times },
        });

        if (this.scheduledPostTimes) {
          this.state = 'SELECT-ITEMS';
        } else {
          this.$emit('modal-close');
        }
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(this.$t('campaign.scheduled-posts.failed-to-save-times'));
      }
    },
    selectAllItems() {
      const { jobs, contentShares } = this.api || {};

      const allRows = mixItems([ 1, 2 ], ...[
        Array.isArray(contentShares) ? contentShares.map(entry => ({ type: 'ContentShare', id: entry.id, entry })) : [],
        Array.isArray(jobs) ? jobs.map(entry => ({ type: 'Job', id: entry.id, entry })) : [],
      ]);

      this.selectedItems = allRows.map(({ type, id, entry }) => ({
        type, id, entry, // eslint-disable-line object-property-newline
        shareURL: createShareURL(this, type, id, entry),
        vars: createTemplateVars(type, entry),
      }));
    },
    deselectAllItems() {
      this.selectedItems = [];
      this.shareTextOverrides = {};
    },
    toggleSelectedItem(type, id, entry) {
      const i = this.selectedItems.findIndex(r => r && r.type === type && r.id === id);
      if (i >= 0) {
        this.selectedItems.splice(i, 1);
        delete this.shareTextOverrides[`${type}-${id}`];
      } else {
        this.selectedItems.push({
          type, id, entry, // eslint-disable-line object-property-newline
          shareURL: createShareURL(this, type, id, entry),
          vars: createTemplateVars(type, entry),
        });
      }
    },
    createTemplateString(template, vars) {
      return Object.keys(vars || {}).reduce((str, key) => str.replace(`{${key}}`, vars[key]), `${template}`);
    },
    async saveScheduledPosts() {
      this.waiting = true;

      try {
        const create = this.selectedItems.reduce((list, { type, id, shareURL, vars }) => {
          switch (type) {
            case 'Job': {
              const defaultShareText = this.createTemplateString(this.jobShareTemplate, vars);

              list.push({
                provider: 'LINKEDIN',
                ...(this.eventID ? { event: { id: this.eventID } } : undefined),
                job: { id },
                text: this.shareTextOverrides[`${type}-${id}`] || defaultShareText || null,
                url: shareURL,
                // sendAt,
              });
              break;
            }
            case 'ContentShare': {
              const defaultShareText = this.createTemplateString(this.contentShareTemplate, vars);

              list.push({
                provider: 'LINKEDIN',
                ...(this.eventID ? { event: { id: this.eventID } } : undefined),
                contentShare: { id },
                text: this.shareTextOverrides[`${type}-${id}`] || defaultShareText || null,
                url: shareURL,
                // sendAt,
              });
              break;
            }
          }

          return list;
        }, []);

        this.$log.debug({ create });

        await this.$apollo.mutate({
          mutation: SaveSchedulePostsMutation,
          variables: { create },
        });

        this.$root.captureLogs([
          ...create.filter(c => c && c.job && c.job.id).map(({ event, job }) => ({
            eventName: 'JOB_SCHEDULE_SHARE',
            user: { self: true },
            event,
            job,
          })),
          ...create.filter(c => c && c.contentShare && c.contentShare.id).map(({ event, contentShare }) => ({
            eventName: 'CONTENT_SCHEDULE_SHARE',
            user: { self: true },
            event,
            contentShare,
          })),
        ]);

        this.state = 'FINISHED';
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr('Failed to schedule post');
      } finally {
        this.waiting = false;
      }
    },
  },
};

function createShareURL(vm, type, id, entry) {
  try {
    const { eventID, origin, trackingCode } = vm;
    const { shareCode } = entry;
    vm.$assert(origin, 'Expected origin to be a string');
    vm.$assert(shareCode, 'Expected shareCode to be a string');

    const prefix = type === 'ContentShare' ? '/c/' : '/r/';
    const extension = trackingCode ? `?${trackingCode}` : '';
    return `${origin}${eventID ? '/e' : ''}${prefix}${eventID ? `${eventID}/` : ''}${shareCode}${extension}`;
  } catch (err) {
    vm.$log.error(`Failed to create shareURL for ${type}:${id}`, err);
    return null;
  }
}

function createTemplateVars(type, entry) {
  switch (type) {
    case 'Job': {
      const { id, title } = entry;
      return { jobID: id, jobTitle: title };
    }
    case 'ContentShare': {
      const { id, title } = entry;
      return { contentID: id, contentTitle: title };
    }
  }
}

function mixItems(pattern, ...lists) {
  assert(Array.isArray(pattern), 'Expected pattern to be an array');
  assert(pattern.length === lists.length, 'Expected pattern to be the length of lists array');

  const output = [];

  const chunks = lists.map((list, i) => {
    assert(typeof i === 'number', 'Expected each pattern to be a chunk size');
    return _chunk(list, pattern[i]);
  });

  const largestArray = chunks.reduce((count, chunk) => chunk.length > count ? chunk.length : count, 0);
  for (let i = 0; i < largestArray; i++) {
    chunks.filter(c => Array.isArray(c[i])).forEach(c => output.push(...c[i]));
  }

  return output;
}
</script>



// WEBPACK FOOTER //
// src/modals/schedule-posts.vue